import { FC, useEffect, useMemo } from 'react';
import {
  Alert,
  Button,
  CheckboxGroupInput,
  DataLossPrompt,
  Footer,
  Loader,
  MultiLanguageInputContainer,
  NothingFoundAlert,
  NumberInput,
  Panel,
  RichTextInput,
  SelectInput,
  SwitchInput,
  TextInput,
  Typography,
} from 'melp-design/components';
import { Box, Collapse, Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  ExpirationNotificationMethod,
  RecognitionLimit,
  RecognitionPoint,
  RecognitionsSettings as RecognitionsSettingsType,
  expirationNotificationMethods,
  recognitionLimits,
  recognitionPoints,
  useRecognitionsSettings,
  useTriggerRecognitionsExpiration,
  useUpdateRecognitionsSettings,
} from 'store/recognitions';
import {
  useCompanyLanguages,
  useDefaultCurrency,
  useMe,
} from 'state/Administrators';
import { usePredefinedToasts } from 'utils/Toast';
import { formatCurrency, formatLanguage, formatMonth } from 'utils/format';
import { APP } from 'config';
import {
  Controller,
  SubmitHandler,
  useFieldArray,
  useForm,
} from 'react-hook-form-latest';
import { Delete, Edit } from '@mui/icons-material';
import { ContentLanguage, FrequencyUnit, frequencyUnits } from 'types/general';
import { Level, Translations } from 'types/Common';
import { getDaysInMonth } from 'utils/general';
import { useConfirm } from 'store/confirm';
import { useModalContext } from 'store/modal';
import { ALLOCATION_PRIORITY_BY_LEVEL } from 'store/benefits';
import {
  AiRuleModal,
  SourceOfPointsModal,
  STRICTNESS_BY_KEY,
  StrictnessKey,
} from './atoms';

interface Values {
  pointRewardPerRecognition: number;
  pointName: RecognitionPoint;
  currencyToPoint: number;
  useMoney: boolean;
  restrictionsApply: boolean;
  limit: RecognitionLimit;
  limitValue: number;
  limitPeriod: FrequencyUnit | 'none';
  languages: Array<Uppercase<ContentLanguage>>;
  expirationEnabled: boolean;
  expirationDay: number;
  expirationMonth: number;
  expirationNotificationInDays: number;
  expirationNotification: ExpirationNotificationMethod[];
  showPointsInFeed: boolean;
  allowComments: boolean;
  allowReimbursementClaim: boolean;
  allocationPriority: number;
  suggestionRules: RecognitionsSettingsType['suggestionRules'];
  translations: Translations<{ description: string; feedName: string }>;
}

const getInitialValues = (
  defaultLanguage: Uppercase<ContentLanguage>,
): Values => ({
  pointName: 'points',
  useMoney: false,
  currencyToPoint: 1,
  pointRewardPerRecognition: 1,
  restrictionsApply: false,
  limit: 'perEmployee',
  limitValue: 1,
  limitPeriod: 'daily',
  expirationEnabled: false,
  expirationDay: 1,
  expirationMonth: 1,
  expirationNotificationInDays: 0,
  expirationNotification: [],
  languages: [defaultLanguage],
  showPointsInFeed: true,
  allowComments: true,
  allowReimbursementClaim: true,
  allocationPriority: ALLOCATION_PRIORITY_BY_LEVEL['medium'],
  suggestionRules: [
    {
      name: '',
      description: '',
      enabled: true,
      strictness: 0.5,
    },
  ],
  translations: [{ language: defaultLanguage, description: '', feedName: '' }],
});

export const RecognitionsSettings: FC = () => {
  const { t, i18n } = useTranslation();
  const predefinedToasts = usePredefinedToasts();
  const { hasEditPermission } = useMe();
  const canEdit = hasEditPermission(['employeeRecognition']);
  const { defaultLanguage, supportedLanguages } = useCompanyLanguages();
  const { confirmation } = useConfirm();
  const { openModal } = useModalContext();
  const { currency } = useDefaultCurrency();

  const INITIAL_VALUES = useMemo(
    () => getInitialValues(defaultLanguage),
    [defaultLanguage],
  );

  const { isFeatureEnabled } = useMe();
  const { data: settings, isLoading } = useRecognitionsSettings();

  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    reset,
    watch,
    formState: { errors, dirtyFields },
  } = useForm({ defaultValues: INITIAL_VALUES });
  const { fields: translationsFields, replace: replaceTranslations } =
    useFieldArray({ name: 'translations', control });
  const {
    fields: suggestionRulesFields,
    append: appendSuggestionRule,
    update: updateSuggestionRule,
    remove: removeSuggestionRule,
  } = useFieldArray({
    name: 'suggestionRules',
    control,
  });

  useEffect(() => {
    if (settings) {
      reset({
        pointName: settings.pointName,
        useMoney: settings.useMoney,
        currencyToPoint: settings.currencyToPoint,
        pointRewardPerRecognition: settings.pointRewardPerRecognition,
        restrictionsApply: settings.limit === 'none' ? false : true,
        expirationEnabled: settings.expirationEnabled,
        expirationDay: settings.expirationDay ?? 1,
        expirationMonth: settings.expirationMonth ?? 1,
        expirationNotificationInDays: settings.expirationNotificationInDays,
        expirationNotification: settings.expirationNotification,
        showPointsInFeed: settings.showPointsInFeed,
        allowComments: settings.allowComments,
        allowReimbursementClaim: settings.allowReimbursementClaim,
        allocationPriority: settings.allocationPriority,
        suggestionRules: settings.suggestionRules.map((rule) => ({
          ...rule,
          strictness: rule.strictness / 100,
        })),
        ...(settings.limit === 'none'
          ? {
              limit: INITIAL_VALUES.limit,
              limitValue: INITIAL_VALUES.limitValue,
              limitPeriod: INITIAL_VALUES.limitPeriod,
            }
          : {
              limit: settings.limit,
              limitValue: settings.limitValue,
              limitPeriod: settings.limitPeriod,
            }),
        ...(settings.translations.length
          ? {
              languages: settings.translations.map((trans) => trans.language),
              translations: settings.translations,
            }
          : {
              languages: INITIAL_VALUES.languages,
              translations: INITIAL_VALUES.translations,
            }),
      });
    }
  }, [INITIAL_VALUES, reset, settings]);

  const { mutate: updateSettings, isLoading: isUpdating } =
    useUpdateRecognitionsSettings();
  const { mutate: triggerExpiration, isLoading: isTriggeringExpiration } =
    useTriggerRecognitionsExpiration();

  const onSubmit: SubmitHandler<Values> = ({
    restrictionsApply,
    suggestionRules,
    limit,
    limitValue,
    limitPeriod,
    languages,
    ...values
  }) => {
    updateSettings(
      {
        ...values,
        suggestionRules: suggestionRules.map((rule) => ({
          ...rule,
          strictness: rule.strictness * 100,
        })),
        ...(restrictionsApply
          ? { limit, limitValue, limitPeriod }
          : { limit: 'none', limitValue: 0, limitPeriod: 'none' }),
      },
      {
        onSuccess: () => {
          predefinedToasts.success.updated();
        },
      },
    );
  };

  if (isLoading) return <Loader />;
  if (!settings) return <NothingFoundAlert />;

  const isDirty = !!Object.keys(dirtyFields).length;

  const [
    pointName,
    useMoney,
    restrictionsApply,
    expirationEnabled,
    expirationMonth,
    languages,
    translations,
  ] = watch([
    'pointName',
    'useMoney',
    'restrictionsApply',
    'expirationEnabled',
    'expirationMonth',
    'languages',
    'translations',
  ]);

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack spacing={2}>
          <Panel title={t('common.settings')}>
            <Stack spacing={2}>
              <Controller
                name="useMoney"
                control={control}
                render={({ field: { name, value, onChange, ref } }) => {
                  return (
                    <SwitchInput
                      label={t('recognition.form.useMoney.label')}
                      name={name}
                      value={value}
                      onChange={onChange}
                      disabled={!canEdit}
                      ref={ref}
                    />
                  );
                }}
              />

              {!isFeatureEnabled('marketplace') &&
              isFeatureEnabled('recognition') &&
              useMoney ? (
                <Alert severity="warning">
                  {t('recognition.form.useMoney.info', {
                    email: APP.email.info,
                  })}
                </Alert>
              ) : null}
              {!useMoney ? (
                <Alert severity="warning">
                  {t('recognition.form.useMoney.info2')}
                </Alert>
              ) : null}

              <Stack gap={1}>
                <Typography variant="p2" color="textSecondary">
                  {t('recognition.exchangeRate')}
                </Typography>
                <Stack
                  direction="row"
                  alignItems="center"
                  sx={{
                    '& > *:first-of-type': {
                      width: '150px',
                      '.MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline':
                        {
                          borderTopRightRadius: '0',
                          borderBottomRightRadius: '0',
                        },
                    },
                    '& > *:nth-of-type(2)': {
                      width: '150px',
                      '.MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline':
                        {
                          borderTopLeftRadius: '0',
                          borderBottomLeftRadius: '0',
                          borderLeft: '0',
                        },
                    },
                  }}
                >
                  <Controller
                    name="currencyToPoint"
                    control={control}
                    rules={{
                      min: {
                        value: 0,
                        message: t('common.minValueValidationError', {
                          min: 0,
                        }),
                      },
                    }}
                    render={({ field: { name, value, onChange, ref } }) => (
                      <NumberInput
                        name={name}
                        value={value}
                        onChange={onChange}
                        errorMessage={errors.currencyToPoint?.message}
                        type="float"
                        required
                        disabled={!canEdit}
                        ref={ref}
                        key={value}
                      />
                    )}
                  />

                  <Controller
                    name="pointName"
                    control={control}
                    render={({ field: { name, value, onChange, ref } }) => {
                      return (
                        <SelectInput
                          name={name}
                          value={value}
                          onChange={onChange}
                          options={
                            recognitionPoints.map((type) => ({
                              label: t(`recognition.pointNames-${type}`),
                              value: type,
                            })) ?? []
                          }
                          required
                          disabled={!canEdit}
                          ref={ref}
                        />
                      );
                    }}
                  />

                  <Typography variant="p1" sx={{ ml: 1 }}>{`= ${formatCurrency(
                    i18n.language,
                    100,
                    currency,
                  )}`}</Typography>
                </Stack>
              </Stack>

              <Controller
                name="allocationPriority"
                control={control}
                render={({ field: { name, value, onChange, ref } }) => {
                  return (
                    <SelectInput
                      label={t('benefits.allocationPriority.label')}
                      infoMessage={t('benefits.allocationPriority.info')}
                      name={name}
                      value={value}
                      onChange={onChange}
                      options={Object.entries(ALLOCATION_PRIORITY_BY_LEVEL).map(
                        ([key, value]) => ({
                          label: t(`common.level.${key as Level}`),
                          value,
                        }),
                      )}
                      disabled={!canEdit}
                      ref={ref}
                    />
                  );
                }}
              />

              <Controller
                name="pointRewardPerRecognition"
                control={control}
                render={({ field: { name, value, onChange, ref } }) => (
                  <NumberInput
                    label={t('recognition.pointRewardPerRecognition')}
                    name={name}
                    value={value}
                    onChange={onChange}
                    required
                    disabled={!canEdit}
                    ref={ref}
                    key={value}
                  />
                )}
              />

              <Controller
                name="showPointsInFeed"
                control={control}
                render={({ field: { name, value, onChange, ref } }) => {
                  return (
                    <SwitchInput
                      label={t('recognitions.showPointsInFeed.label', {
                        pointsLabel: t(`recognition.pointNames-${pointName}`),
                      })}
                      name={name}
                      value={value}
                      onChange={onChange}
                      disabled={!canEdit}
                      ref={ref}
                    />
                  );
                }}
              />

              <Controller
                name="allowComments"
                control={control}
                render={({ field: { name, value, onChange, ref } }) => {
                  return (
                    <SwitchInput
                      label={t('news.allowComments.label')}
                      name={name}
                      value={value}
                      onChange={onChange}
                      disabled={!canEdit}
                      ref={ref}
                    />
                  );
                }}
              />

              <Controller
                name="allowReimbursementClaim"
                control={control}
                render={({ field: { name, value, onChange, ref } }) => {
                  return (
                    <SwitchInput
                      label={t('benefits.allowClaimReimbursement.label')}
                      name={name}
                      value={value}
                      onChange={onChange}
                      disabled={!canEdit}
                      ref={ref}
                    />
                  );
                }}
              />

              <Box>
                <Button
                  label={t(
                    'companies.settings.sourceOfRecognitionPoints.label',
                  )}
                  variant="tertiary"
                  onClick={() => {
                    openModal({
                      component: SourceOfPointsModal,
                      size: 'lg',
                    });
                  }}
                  disabled={
                    isTriggeringExpiration ||
                    !hasEditPermission(['employeeRecognition'])
                  }
                />
              </Box>
            </Stack>
          </Panel>

          <Panel title={t('recognition.limits.label')}>
            <Stack gap={2}>
              <Controller
                name="restrictionsApply"
                control={control}
                render={({ field: { name, value, onChange, ref } }) => {
                  return (
                    <SwitchInput
                      label={t('recognitions.limit.label')}
                      name={name}
                      value={value}
                      onChange={onChange}
                      disabled={!canEdit}
                      ref={ref}
                    />
                  );
                }}
              />

              <Collapse in={restrictionsApply}>
                <Stack flexDirection="row" alignItems="center" gap={1}>
                  <Typography noWrap>
                    {t('recognitions.limit.employee_to')}
                  </Typography>

                  <Controller
                    name="limit"
                    control={control}
                    render={({ field: { name, value, onChange, ref } }) => {
                      return (
                        <SelectInput
                          name={name}
                          value={value}
                          onChange={onChange}
                          options={recognitionLimits
                            .filter((limit) => limit !== 'none')
                            .map((limit) => ({
                              label: t(`recognitions.limit.${limit}`),
                              value: limit,
                            }))}
                          required
                          disabled={!canEdit}
                          ref={ref}
                        />
                      );
                    }}
                  />

                  <Controller
                    name="limitValue"
                    control={control}
                    render={({ field: { name, value, onChange, ref } }) => {
                      return (
                        <NumberInput
                          name={name}
                          value={value}
                          onChange={onChange}
                          required
                          sx={{ minWidth: 75 }}
                          key={value}
                          disabled={!canEdit}
                          ref={ref}
                        />
                      );
                    }}
                  />

                  <Controller
                    name="limitPeriod"
                    control={control}
                    render={({ field: { name, value, onChange, ref } }) => {
                      return (
                        <SelectInput
                          name={name}
                          value={value}
                          onChange={onChange}
                          options={frequencyUnits
                            .filter(
                              (period) =>
                                !['once', 'hourly', 'lifetime'].includes(
                                  period,
                                ),
                            )
                            .map((period) => ({
                              label: t(
                                `recognition.allowanceRuleFrequency-${period}`,
                              ),
                              value: period,
                            }))}
                          required
                          disabled={!canEdit}
                          ref={ref}
                        />
                      );
                    }}
                  />
                </Stack>
              </Collapse>
            </Stack>
          </Panel>

          <Panel
            title={t('recognitions.expiration.title', {
              pointsLabel: t(`recognition.pointNames-${pointName}`),
            })}
          >
            <Stack gap={2}>
              <Controller
                name="expirationEnabled"
                control={control}
                render={({ field: { name, value, onChange, ref } }) => {
                  return (
                    <SwitchInput
                      label={t('recognitions.expirationEnabled.label', {
                        pointsLabel: t(`recognition.pointNames-${pointName}`),
                      })}
                      name={name}
                      value={value}
                      onChange={onChange}
                      disabled={!canEdit}
                      ref={ref}
                    />
                  );
                }}
              />

              <Collapse in={expirationEnabled}>
                <Stack gap={2}>
                  <Typography color="textSecondary">
                    {t('recognitions.expiresOn.label')}
                  </Typography>

                  <Stack flexDirection="row" alignItems="center" gap={1}>
                    <Controller
                      name="expirationMonth"
                      control={control}
                      render={({ field: { name, value, onChange, ref } }) => {
                        return (
                          <SelectInput
                            name={name}
                            value={value}
                            onChange={(v) => {
                              onChange(v);
                              setValue(
                                'expirationDay',
                                INITIAL_VALUES.expirationDay,
                              );
                            }}
                            options={[...Array(12).keys()].map((key) => ({
                              label: formatMonth(i18n.language, key),
                              value: key + 1,
                            }))}
                            required
                            disabled={!canEdit}
                            ref={ref}
                          />
                        );
                      }}
                    />

                    <Controller
                      name="expirationDay"
                      control={control}
                      render={({ field: { name, value, onChange, ref } }) => {
                        return (
                          <SelectInput
                            name={name}
                            value={value}
                            onChange={onChange}
                            options={[
                              ...Array(getDaysInMonth(expirationMonth)).keys(),
                            ].map((month) => ({
                              label: String(month + 1),
                              value: month + 1,
                            }))}
                            required
                            disabled={!canEdit}
                            ref={ref}
                          />
                        );
                      }}
                    />
                  </Stack>

                  <Controller
                    name="expirationNotificationInDays"
                    control={control}
                    rules={{
                      min: {
                        value: 0,
                        message: t('common.minValueValidationError', {
                          min: 0,
                        }),
                      },
                    }}
                    render={({ field: { name, value, onChange, ref } }) => (
                      <NumberInput
                        label={t(
                          'recognitions.expirationNotificationInDays.label',
                        )}
                        name={name}
                        value={value}
                        onChange={onChange}
                        errorMessage={
                          errors.expirationNotificationInDays?.message
                        }
                        type="integer"
                        required
                        sx={{ maxWidth: '50%' }}
                        disabled={!canEdit}
                        ref={ref}
                        key={value}
                      />
                    )}
                  />

                  <Controller
                    name="expirationNotification"
                    control={control}
                    render={({ field: { name, value, onChange, ref } }) => {
                      return (
                        <CheckboxGroupInput
                          label={t('recognitions.expirationNotification.label')}
                          items={expirationNotificationMethods.map(
                            (method) => ({
                              label: t(`news.send_method.${method}`),
                              value: method,
                            }),
                          )}
                          name={name}
                          value={value}
                          onChange={onChange}
                          disabled={!canEdit}
                          ref={ref}
                        />
                      );
                    }}
                  />

                  <Box>
                    <Button
                      label={t('recognitions.actions.trigger_expiration')}
                      variant="danger-outline"
                      onClick={async () => {
                        const { confirmed } = await confirmation(
                          t('recognitions.info.trigger_expiration'),
                          undefined,
                          'danger',
                        );

                        if (confirmed) {
                          triggerExpiration(undefined, {
                            onSuccess: () => {
                              predefinedToasts.success.updated();
                            },
                          });
                        }
                      }}
                      disabled={
                        isTriggeringExpiration ||
                        !hasEditPermission(['employeeRecognition'])
                      }
                    />
                  </Box>
                </Stack>
              </Collapse>
            </Stack>
          </Panel>

          <Panel title={t('benefitTemplates.templateContent')}>
            <Stack gap={2}>
              <Controller
                name="languages"
                control={control}
                render={({ field: { name, value, onChange, ref } }) => {
                  return (
                    <SelectInput
                      label={t('companies.supported_languages')}
                      name={name}
                      value={value}
                      onChange={(value) => {
                        const selectedLanguages = value as Array<
                          Uppercase<ContentLanguage>
                        >;
                        replaceTranslations(
                          selectedLanguages.map(
                            (language) =>
                              translations.find(
                                (t) => t.language === language,
                              ) ?? {
                                ...INITIAL_VALUES.translations[0],
                                language,
                              },
                          ),
                        );
                        onChange(selectedLanguages);
                      }}
                      options={supportedLanguages.map((language) => ({
                        value: language,
                        label: formatLanguage(i18n.language, language),
                        disabled:
                          languages.includes(language) &&
                          language === defaultLanguage,
                      }))}
                      multiple
                      required
                      disabled={!canEdit}
                      ref={ref}
                    />
                  );
                }}
              />

              <MultiLanguageInputContainer
                label={t('common.rules.title')}
                fields={translationsFields.map((field, i) => ({
                  id: field.id,
                  language: field.language,
                  component: (
                    <Controller
                      name={`translations.${i}.description`}
                      control={control}
                      render={({ field: { value, onChange, ref } }) => {
                        return (
                          <RichTextInput
                            value={value}
                            onChange={onChange}
                            ai={
                              isFeatureEnabled('ai')
                                ? {
                                    inputLanguage: defaultLanguage,
                                    outputLanguage: field.language,
                                    text:
                                      getValues('translations').find(
                                        (t) => t.language === defaultLanguage,
                                      )?.description ?? '',
                                  }
                                : undefined
                            }
                            disabled={!canEdit}
                            ref={ref}
                          />
                        );
                      }}
                    />
                  ),
                }))}
              />

              <MultiLanguageInputContainer
                label={t('recognitions.feedName.label')}
                fields={translationsFields.map((field, i) => ({
                  id: field.id,
                  language: field.language,
                  component: (
                    <Controller
                      name={`translations.${i}.feedName`}
                      control={control}
                      render={({ field: { name, value, onChange, ref } }) => {
                        return (
                          <TextInput
                            name={name}
                            value={value}
                            onChange={onChange}
                            ai={
                              isFeatureEnabled('ai')
                                ? {
                                    inputLanguage: defaultLanguage,
                                    outputLanguage: field.language,
                                    text:
                                      getValues('translations').find(
                                        (t) => t.language === defaultLanguage,
                                      )?.feedName ?? '',
                                  }
                                : undefined
                            }
                            disabled={!canEdit}
                            ref={ref}
                          />
                        );
                      }}
                    />
                  ),
                }))}
              />
            </Stack>
          </Panel>

          {isFeatureEnabled('ai') ? (
            <Panel title={t('clients.feature-ai')}>
              <Stack gap={2}>
                <Typography color="textSecondary">
                  {t('recognitions.suggestionRules.label')}
                </Typography>

                <Stack gap={2}>
                  {suggestionRulesFields.map((field, i) => {
                    const strictness = Object.entries(STRICTNESS_BY_KEY).find(
                      ([_, value]) => value === field.strictness,
                    );

                    return (
                      <Stack
                        direction="row"
                        alignItems="center"
                        gap={4}
                        key={i}
                      >
                        <Controller
                          name={`suggestionRules.${i}.enabled`}
                          control={control}
                          render={({
                            field: { name, value, onChange, ref },
                          }) => (
                            <SwitchInput
                              label={t('status.enabled')}
                              name={name}
                              value={value}
                              onChange={onChange}
                              disabled={!canEdit}
                              ref={ref}
                            />
                          )}
                        />

                        <Stack flex={1}>
                          <Typography variant="h3">{field.name}</Typography>
                          {strictness ? (
                            <Typography color="textSecondary" variant="h3">
                              {t(
                                `recognitions.suggestionRules.strictness.${
                                  strictness[0] as StrictnessKey
                                }`,
                              )}
                            </Typography>
                          ) : null}
                          <Typography color="textSecondary">
                            {field.description}
                          </Typography>
                        </Stack>

                        <Stack flexDirection="row" gap={2}>
                          <Button
                            variant="neutral-outline"
                            size="md"
                            icon={Edit}
                            disabled={!canEdit}
                            onClick={async () => {
                              const response = await openModal({
                                component: AiRuleModal,
                                props: {
                                  initialValues: {
                                    name: field.name,
                                    description: field.description,
                                    enabled: field.enabled,
                                    strictness: field.strictness,
                                  },
                                },
                              });

                              if (response.action === 'CONFIRM') {
                                updateSuggestionRule(i, {
                                  name: response.values.name,
                                  description: response.values.description,
                                  enabled: response.values.enabled,
                                  strictness: response.values.strictness,
                                });
                              }
                            }}
                            title={t('common.edit')}
                          />

                          <Button
                            variant="neutral-outline"
                            size="md"
                            icon={Delete}
                            disabled={!canEdit}
                            onClick={() => removeSuggestionRule(i)}
                            title={t('common.remove')}
                          />
                        </Stack>
                      </Stack>
                    );
                  })}
                </Stack>

                <Box>
                  <Button
                    label={t('automation.addRule')}
                    variant="tertiary"
                    disabled={!canEdit}
                    onClick={async () => {
                      const response = await openModal({
                        component: AiRuleModal,
                      });

                      if (response.action === 'CONFIRM') {
                        appendSuggestionRule({
                          name: response.values.name,
                          description: response.values.description,
                          enabled: response.values.enabled,
                          strictness: response.values.strictness,
                        });
                      }
                    }}
                  />
                </Box>
              </Stack>
            </Panel>
          ) : null}
        </Stack>

        <Footer visible={isDirty}>
          <Stack direction="row" justifyContent="end" gap={2}>
            <Button
              label={t('common.cancel')}
              variant="neutral-outline"
              onClick={() => {
                reset(undefined, { keepDirty: false });
              }}
            />
            <Button
              label={t('common.save')}
              variant="primary"
              type="submit"
              disabled={
                isUpdating || !hasEditPermission(['employeeRecognition'])
              }
            />
          </Stack>
        </Footer>
      </form>

      <DataLossPrompt
        when={!hasEditPermission(['employeeRecognition']) ? false : isDirty}
      />
    </>
  );
};
